import React, { Suspense } from 'react';
import cx from 'classnames';

import Modal from '~/components/modals/modal';
import AddCreditCardForm from '~/components/account/settings/add-credit-card-form';
import PaymentMethodSummary from '~/components/cart/payment-method-summary';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { PaymentTypes } from '~/features/stripe';
import { getDefaultCard } from '~/features/cart/cart';
import { useUpdatePaymentMethod } from '~/features/payment-methods/payment-method-actions';
import { useCart } from '~/hooks/use-cart';
import { useSession } from '~/features/session/session-actions';
import { safeLazy } from '~/utils/safe-lazy';

import styles from '~/components/modals/modal.module.scss';

const StripeProvider = safeLazy(
  () => import('~/components/form/stripe-provider')
);

const UpdatePaymentModal = ({ onClose, isOpen }) => {
  const { cart, defaultCard } = useCart();
  const { user } = useSession();
  const { showFlashMessage } = useFlashMessage();
  const { updatePaymentMethod } = useUpdatePaymentMethod();

  const cardOnFile = getDefaultCard(cart, defaultCard);

  const handleAddCreditCardSubmit = async (paymentMethodId) => {
    try {
      await updatePaymentMethod(paymentMethodId);

      showFlashMessage('Your payment type has been updated.', 'success');

      onClose();
    } catch (err) {
      return showFlashMessage(err.errors?.stripeError, 'error');
    }
  };

  const isCreditCard =
    cardOnFile?.sourceType?.toLowerCase() === PaymentTypes.CARD;

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      className={styles.accountInfoModal}
    >
      <h1 className={cx(styles.title, styles.accountInfoModalTitle)}>
        Payment info
      </h1>
      {cardOnFile && (
        <CreditCardBanner cardInfo={cardOnFile} isCreditCard={isCreditCard} />
      )}
      <Suspense fallback={null}>
        <StripeProvider mode="setup">
          <AddCreditCardForm onSubmit={handleAddCreditCardSubmit} user={user} />
        </StripeProvider>
      </Suspense>
    </Modal>
  );
};

export function CreditCardBanner({ cardInfo, variant, isCreditCard }) {
  return (
    <div
      className={cx(styles.cardOnFileBanner, {
        [styles[`cardOnFileBanner--${variant}`]]: Boolean(variant),
      })}
    >
      <p className={styles.cardOnFileLabel}>
        Current {isCreditCard ? 'card' : 'payment method'} on file
      </p>
      <div className={styles.cardOnFileContainer}>
        <PaymentMethodSummary paymentMethod={cardInfo} />
        {isCreditCard && (
          <span>
            exp {cardInfo.expMonth}/{cardInfo.expYear}
          </span>
        )}
      </div>
    </div>
  );
}

export default UpdatePaymentModal;
